import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorCode, errorMessage, isErrorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { FollowUpRequestForm } from '@/follow-up/FollowUpRequestForm';
import { useFollowUpsAlert } from '@/follow-up/FollowUpsAlertContext';
import {
  StudentSelection,
  studentOptionLabel,
} from '@/follow-up/StudentSelection';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
} from '~/hooks/useAsyncPeoplePicker';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';

type GroupFollowUpRequestCreate =
  components['schemas']['GroupFollowUpRequestCreate'];

type UserIDSearchParams = { user_id?: number };

/**
 * The message for each of the create call's refusals, keyed by `error_code`.
 * `follow_up_request_already_open` isn't here because it has its own alert
 * above the subnav, as in legacy.
 */
const CREATE_ERROR_KEYS = {
  follow_up_objective_required: 'wbs.you-must-select-a-new-follow-up-objective',
  follow_up_country_required: 'wbs.please-select-a-country',
  follow_up_address_required: 'wbs.please-enter-an-address',
  follow_up_consent_required:
    'wbs.please-make-sure-you-have-confirmed-to-all-the-permissions',
} as const;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/follow-up-requests/new',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): UserIDSearchParams => {
    const id = Number(search.user_id);
    return { user_id: Number.isInteger(id) && id > 0 ? id : undefined };
  },
});

/**
 * The two-step Create a New Request wizard, branching on `?user_id=` as legacy
 * does.
 *
 * As in legacy, the duplicate check runs on step 1 only, so a direct link to
 * step 2 for a student with an open request still renders the form; the create
 * then answers 409.
 */
function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { groupName } = Route.useParams();
  const { setDuplicateBlocked, setCreatedRequestId } = useFollowUpsAlert();

  useEffect(() => () => setDuplicateBlocked(false), [setDuplicateBlocked]);

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  // What the person is typing, and what the query is keyed on. Debounced at
  // 250ms, as legacy's select2 is.
  const [term, setTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setSearchTerm(term),
      PEOPLE_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [term]);

  const { data: matches, isFetching: matchesFetching } =
    api.listGroupFollowUpRequestStudentOptions.useQuery(
      {
        path: { group_id: groupId ?? 0 },
        query: { search: searchTerm, limit: PEOPLE_PICKER_PAGE_SIZE },
      },
      {
        enabled:
          groupId !== undefined && searchTerm.length >= PEOPLE_PICKER_MIN_QUERY,
      },
    );

  const results = useMemo<Person[]>(
    () =>
      (matches?.data ?? []).map((option) => ({
        id: option.id,
        name: studentOptionLabel(option.display_name, option.id),
      })),
    [matches],
  );

  // The student picked on step 1, held until the draft says whether they
  // already have a live request. After navigation the id comes from the URL, so
  // the same draft query serves both and a candidate equal to `?user_id=` is no
  // longer pending.
  //
  // A candidate that differs wins over the URL: the recovery picker only shows
  // when `?user_id=` names a student whose draft 404s, and keying on that id
  // would fail every replacement pick.
  const [candidateId, setCandidateId] = useState<number | null>(null);
  const pendingCandidateId =
    candidateId !== null && candidateId !== search.user_id ? candidateId : null;
  const targetStudentId = pendingCandidateId ?? search.user_id ?? null;

  const {
    data: draft,
    isError: draftFailed,
    error: draftError,
  } = api.getGroupFollowUpRequestDraft.useQuery(
    {
      path: { group_id: groupId ?? 0 },
      query: { student_id: targetStudentId ?? 0 },
    },
    { enabled: groupId !== undefined && targetStudentId !== null },
  );

  useEffect(() => {
    if (candidateId === null) return;
    // Held until the navigation has landed, not just been requested: clearing
    // it beside `navigate()` flashes step 1 for a render.
    if (search.user_id === candidateId) {
      setCandidateId(null);
      return;
    }
    if (draftFailed) {
      setCandidateId(null);
      return;
    }
    if (draft === undefined) return;
    // Shown on the step-1 submit, above the subnav, as in legacy. The create
    // still 409s.
    if (draft.has_live_request) {
      setCandidateId(null);
      setDuplicateBlocked(true);
      return;
    }
    void navigate({ to: './', search: { user_id: candidateId } });
  }, [
    candidateId,
    draft,
    draftFailed,
    navigate,
    search.user_id,
    setDuplicateBlocked,
  ]);

  const {
    mutateAsync: createRequest,
    isPending: creating,
    error: createError,
  } = api.createGroupFollowUpRequest.useMutation();

  const handleClose = () => {
    if (search.user_id !== undefined) {
      void navigate({ to: './', search: {}, replace: true });
      return;
    }
    void navigate({
      to: '/groups/$groupName/follow-ups/follow-up-requests/',
      params: { groupName },
    });
  };

  const handleNext = (studentId: number) => {
    setDuplicateBlocked(false);
    setCandidateId(studentId);
  };

  const handleSubmit = async (
    body: GroupFollowUpRequestCreate,
  ): Promise<void> => {
    if (groupId === undefined) return;
    let created: components['schemas']['GroupFollowUpRequestCreated'];
    try {
      created = await createRequest({ path: { group_id: groupId }, body });
    } catch (error) {
      // The 409 is the duplicate notice, rendered where the step-1 one is; every
      // other refusal is drawn from `createError` beside the form.
      if (isErrorCode(error, 'follow_up_request_already_open')) {
        setDuplicateBlocked(true);
      }
      return;
    }
    setCreatedRequestId(String(created.id));
    // Both group lists can be stale: the new row is pending, unless the student
    // is an unverified postal student, in which case it's auto-approved and
    // lands in the other list.
    await Promise.all([
      api.listGroupPendingFollowUpRequests.invalidateQueries(),
      api.listGroupFollowUpRequests.invalidateQueries(),
    ]);
    // Legacy also lands on the pending list after a create.
    void navigate({
      to: '/groups/$groupName/follow-ups/pending-requests/',
      params: { groupName },
    });
  };

  const createFailureCode = errorCode(createError);
  const createFailureText =
    createError == null ||
    createFailureCode === 'follow_up_request_already_open'
      ? null
      : createFailureCode !== undefined &&
          createFailureCode in CREATE_ERROR_KEYS
        ? t(
            CREATE_ERROR_KEYS[
              createFailureCode as keyof typeof CREATE_ERROR_KEYS
            ],
          )
        : // Translation Needed
          errorMessage(
            createError,
            t('wbs.there-was-a-problem-with-your-submission'),
          );

  const picker = (
    <StudentSelection
      source={{
        results,
        // The control stays loading through the debounce window too, so it does
        // not flash "no results" between a keystroke and the request it caused.
        loading:
          term.length >= PEOPLE_PICKER_MIN_QUERY &&
          (term !== searchTerm || matchesFetching),
        onSearch: setTerm,
      }}
      onNext={handleNext}
      submitting={pendingCandidateId !== null}
    />
  );

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.new-follow-up-request')}
          placement="follow_ups"
        />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleClose} />
        {createFailureText !== null && (
          <AlertBox variant="error">{createFailureText}</AlertBox>
        )}
        {search.user_id === undefined || pendingCandidateId !== null ? (
          // Step 1, including a replacement pick from the recovery picker,
          // whose draft is keyed on the new student.
          picker
        ) : draftFailed ? (
          // The draft 404s for a student outside the group's roster, which a
          // hand-edited `?user_id=` can reach but the picker can't.
          <>
            <AlertBox variant="error">
              {errorMessage(
                draftError,
                t('wbs.there-was-a-problem-with-your-submission'),
              )}
            </AlertBox>
            {picker}
          </>
        ) : draft === undefined ? null : (
          <FollowUpRequestForm
            // Seven fields seed from `draft` in `useState`; the key forces a
            // remount so a student swap can never submit the previous
            // student's contact snapshot.
            key={draft.student.id}
            draft={draft}
            submitting={creating}
            onSubmit={(body) => void handleSubmit(body)}
          />
        )}
      </FormContainer>
    </>
  );
}
